import React, { useEffect, useId, useState } from 'react';
import DsButton from './DsButton';
import { getPaginationItems, normalizePagination } from '../../utils/pagination';
import '../../css/common/ListPagination.css';

type ListPaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  ready?: boolean;
  isLoading?: boolean;
  label?: string;
};

/** Rendering only: the owning list keeps its filters, fetching and URL history. */
export default function ListPagination({
  currentPage,
  totalPages,
  onPageChange,
  ready = true,
  isLoading = false,
  label = '一覧',
}: ListPaginationProps) {
  const { currentPage: page, totalPages: total } = normalizePagination(currentPage, totalPages);
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const [draft, setDraft] = useState(String(page));
  const [error, setError] = useState('');

  useEffect(() => {
    setDraft(String(page));
    setError('');
  }, [page, total]);

  if (!ready || total <= 1) return null;

  const navigate = (target: number) => {
    if (!isLoading && target !== page && target >= 1 && target <= total) onPageChange(target);
  };
  const submitPage = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isLoading) return;
    const value = draft.trim();
    const target = Number(value);
    if (!/^\d+$/.test(value) || !Number.isSafeInteger(target) || target < 1 || target > total) {
      setError(`1〜${total}のページ番号を入力してください。`);
      return;
    }
    setError('');
    setDraft(String(target));
    navigate(target);
  };

  return (
    <div className="list-pagination">
      <nav className="list-pagination__navigation" aria-label={`${label}のページ切り替え`} aria-busy={isLoading}>
        <DsButton variant="secondary" className="list-pagination__direction" aria-label="前のページ" disabled={page === 1 || isLoading} onClick={() => navigate(page - 1)}>
          <i className="fa-solid fa-chevron-left" aria-hidden="true" />
          <span className="list-pagination__direction-label">前へ</span>
        </DsButton>
        <div className="list-pagination__pages">
          {getPaginationItems(page, total).map((item) => typeof item === 'number' ? (
            <DsButton
              key={item}
              variant={item === page ? 'primary' : 'secondary'}
              className="list-pagination__page"
              aria-label={`${item}ページ${item === page ? '（現在のページ）' : ''}`}
              aria-current={item === page ? 'page' : undefined}
              disabled={isLoading}
              onClick={() => navigate(item)}
            >
              {item}
            </DsButton>
          ) : <span key={item} className="list-pagination__gap" aria-hidden="true">…</span>)}
        </div>
        <form className="list-pagination__jump" onSubmit={submitPage} noValidate>
          <label className="list-pagination__sr-only" htmlFor={inputId}>ページ番号</label>
          <input
            className={`input${error ? ' input--invalid' : ''}`}
            id={inputId}
            type="text"
            inputMode="numeric"
            enterKeyHint="go"
            autoComplete="off"
            value={draft}
            disabled={isLoading}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : undefined}
            onChange={(event) => { setDraft(event.target.value); setError(''); }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
              event.preventDefault();
              event.currentTarget.form?.requestSubmit();
            }}
          />
          <span className="list-pagination__total" aria-label={`全${total}ページ`}>/ {total}</span>
          <DsButton type="submit" variant="secondary" disabled={isLoading}>移動</DsButton>
        </form>
        <DsButton variant="secondary" className="list-pagination__direction" aria-label="次のページ" disabled={page === total || isLoading} onClick={() => navigate(page + 1)}>
          <span className="list-pagination__direction-label">次へ</span>
          <i className="fa-solid fa-chevron-right" aria-hidden="true" />
        </DsButton>
      </nav>
      <p className="list-pagination__sr-only" role="status" aria-live="polite" aria-atomic="true">
        {isLoading ? '読み込み中' : `${total}ページ中${page}ページ目`}
      </p>
      {error && <p id={errorId} className="list-pagination__error" role="alert">{error}</p>}
    </div>
  );
}
